<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'max-w-3xl' }">
        <UCard class="h-full flex flex-col">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">
                        Pricing — {{ provider?.name }}
                    </h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="$emit('update:open', false)" />
                </div>
            </template>

            <div class="space-y-8 overflow-y-auto">
                <div class="space-y-3">
                    <div class="flex items-center justify-between">
                        <h4 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Places</h4>
                        <UButton @click="openPlaceForm()" color="black" icon="i-ph-plus-bold" size="xs">Add</UButton>
                    </div>
                    <div v-if="places.length" class="bg-gray-50 rounded-lg overflow-hidden">
                        <table class="w-full text-sm">
                            <thead>
                                <tr class="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                                    <th class="px-3 py-2">Name</th>
                                    <th class="px-3 py-2">Address</th>
                                    <th class="px-3 py-2 text-center">Base</th>
                                    <th class="px-3 py-2 text-center">Local</th>
                                    <th class="px-3 py-2 text-right">One-Way</th>
                                    <th class="px-3 py-2 text-right">Round-Trip</th>
                                    <th class="px-3 py-2 w-px"></th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200">
                                <tr v-for="place in places" :key="place.id">
                                    <td class="px-3 py-2 font-medium">{{ place.name }}</td>
                                    <td class="px-3 py-2 text-gray-500 truncate max-w-[200px]">{{ place.address || '–' }}</td>
                                    <td class="px-3 py-2 text-center">
                                        <UIcon v-if="place.is_base" name="i-ph-check-bold" class="size-4 text-green-500" />
                                    </td>
                                    <td class="px-3 py-2 text-center">
                                        <UIcon v-if="place.is_local_area" name="i-ph-check-bold" class="size-4 text-green-500" />
                                    </td>
                                    <td class="px-3 py-2 text-right font-mono">{{ place.one_way_price != null ? `$${place.one_way_price}` : '–' }}</td>
                                    <td class="px-3 py-2 text-right font-mono">{{ place.round_trip_price != null ? `$${place.round_trip_price}` : '–' }}</td>
                                    <td class="px-3 py-2">
                                        <UDropdown :items="placeActions(place)">
                                            <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-vertical-20-solid" size="xs" />
                                        </UDropdown>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="text-sm text-gray-400 text-center py-6 bg-gray-50 rounded-lg">
                        No places configured.
                    </div>
                </div>

                <div class="space-y-3">
                    <div class="flex items-center justify-between">
                        <h4 class="text-sm font-semibold text-gray-500 uppercase tracking-wide">Distance Zones</h4>
                        <UButton @click="addZoneRow" color="black" icon="i-ph-plus-bold" size="xs">Add</UButton>
                    </div>
                    <div class="bg-gray-50 rounded-lg overflow-hidden">
                        <table class="w-full text-sm">
                            <thead>
                                <tr class="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                                    <th class="px-2 py-2 w-12">#</th>
                                    <th class="px-2 py-2 w-24">From KM</th>
                                    <th class="px-2 py-2">To KM</th>
                                    <th class="px-2 py-2">One-Way $</th>
                                    <th class="px-2 py-2">Round-Trip $</th>
                                    <th class="px-2 py-2">First Leg $</th>
                                    <th class="px-2 py-2 w-px"></th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200">
                                <tr v-for="(zone, i) in zoneRows" :key="i">
                                    <td class="px-2 py-1.5 font-medium text-gray-500">{{ i + 1 }}</td>
                                    <td class="px-2 py-1.5">
                                        <span class="font-mono text-gray-400">{{ zone.min_km }}</span>
                                    </td>
                                    <td class="px-2 py-1.5">
                                        <UInput v-model.number="zone.max_km" type="number" size="sm" step="0.1" placeholder="∞" :ui="{ base: 'font-mono' }" />
                                    </td>
                                    <td class="px-2 py-1.5">
                                        <UInput v-model.number="zone.one_way_price" type="number" size="sm" :ui="{ base: 'font-mono' }" />
                                    </td>
                                    <td class="px-2 py-1.5">
                                        <UInput v-model.number="zone.round_trip_price" type="number" size="sm" :ui="{ base: 'font-mono' }" />
                                    </td>
                                    <td class="px-2 py-1.5">
                                        <UInput v-model.number="zone.first_leg_price" type="number" size="sm" :ui="{ base: 'font-mono' }" />
                                    </td>
                                    <td class="px-2 py-1.5">
                                        <UButton color="gray" variant="ghost" icon="i-ph-trash-fill" size="xs" :ui="{ icon: { size: { xs: 'text-red-400' } } }" @click="zoneRows.splice(i, 1)" />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="!zoneRows.length" class="text-sm text-gray-400 text-center py-6">
                            No distance zones. Click Add to create one.
                        </div>
                    </div>
                    <div v-if="zoneRows.length" class="flex justify-end">
                        <UButton @click="saveZones" color="black" size="sm" :loading="savingZones">Save Zones</UButton>
                    </div>
                </div>
            </div>
        </UCard>

        <UModal v-model="showPlaceForm" :ui="{ width: 'w-full sm:max-w-lg' }">
            <UCard :ui="{ rounded: 'rounded-lg' }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">{{ editingPlace ? 'Edit Place' : 'Add Place' }}</h3>
                        <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="showPlaceForm = false" />
                    </div>
                </template>
                <div class="space-y-4">
                    <UFormGroup label="Name" required>
                        <UInput v-model="placeState.name" size="lg" placeholder="e.g. Centre de vie Oasis" />
                    </UFormGroup>
                    <UFormGroup label="Address">
                        <div class="relative">
                            <UInput
                                v-model="addressSearch"
                                size="lg"
                                placeholder="Search address..."
                                icon="i-ph-magnifying-glass-duotone"
                                :loading="searchingAddress"
                                @focus="addressDropdownOpen = true"
                                @blur="onAddressBlur"
                            />
                            <div v-if="addressDropdownOpen && addressResults.length" class="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black/5 max-h-48 overflow-y-auto">
                                <button
                                    v-for="pred in addressResults"
                                    :key="pred.placeId"
                                    type="button"
                                    @mousedown.prevent="selectAddress(pred)"
                                    class="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                                >
                                    <div class="font-medium text-gray-800 truncate">{{ pred.mainText }}</div>
                                    <div v-if="pred.secondaryText" class="text-xs text-gray-500 truncate">{{ pred.secondaryText }}</div>
                                </button>
                            </div>
                        </div>
                        <p v-if="placeState.lat" class="text-xs text-gray-400 mt-1">
                            {{ placeState.address }} ({{ placeState.lat }}, {{ placeState.lng }})
                        </p>
                    </UFormGroup>
                    <div class="flex items-center space-x-6">
                        <UCheckbox v-model="placeState.is_base" label="Base location" />
                        <UCheckbox v-model="placeState.is_local_area" label="Local area pricing" />
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                        <UFormGroup label="One-Way Price ($)">
                            <UInput v-model.number="placeState.one_way_price" type="number" size="lg" />
                        </UFormGroup>
                        <UFormGroup label="Round-Trip Price ($)">
                            <UInput v-model.number="placeState.round_trip_price" type="number" size="lg" />
                        </UFormGroup>
                    </div>
                </div>
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="showPlaceForm = false" size="lg" color="white">Cancel</UButton>
                        <UButton @click="savePlace" size="lg" color="black" :loading="savingPlace">Save</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>

    </USlideover>
</template>

<script setup lang="ts">
import type { ProviderPlace, ProviderPricingZone, Provider } from '~/types/provider'
import type { PlacePrediction } from '~/composables/useGeocoding'

const emit = defineEmits<{ 'update:open': [boolean] }>()

const props = defineProps<{
    open: boolean
    provider?: Provider
}>()

const client = useSupabaseClient()
const toast = useToast()
const { searchPredictions, getPlaceDetails, createSessionToken } = useGeocoding()

const places = ref<ProviderPlace[]>([])
const zones = ref<ProviderPricingZone[]>([])

const showPlaceForm = ref(false)
const editingPlace = ref<ProviderPlace | null>(null)
const savingPlace = ref(false)
const placeState = ref({ name: '', address: '', lat: null as number | null, lng: null as number | null, is_base: false, is_local_area: false, one_way_price: null as number | null, round_trip_price: null as number | null })

const addressSearch = ref('')
const searchingAddress = ref(false)
const addressResults = ref<PlacePrediction[]>([])
const addressDropdownOpen = ref(false)
const addressTimeout = ref<NodeJS.Timeout>()
const addressSessionToken = ref<string>('')

interface ZoneRow { min_km: number; max_km: number | null; one_way_price: number; round_trip_price: number; first_leg_price: number }
const zoneRows = ref<ZoneRow[]>([])
const savingZones = ref(false)

watch(() => props.open, async (open) => {
    if (open && props.provider) {
        await fetchData()
    }
})

watch(addressSearch, (val) => {
    if (addressTimeout.value) clearTimeout(addressTimeout.value)
    if (!val || val.length < 3) {
        addressResults.value = []
        return
    }
    if (!addressSessionToken.value) addressSessionToken.value = createSessionToken()
    searchingAddress.value = true
    addressTimeout.value = setTimeout(async () => {
        addressResults.value = await searchPredictions(val, addressSessionToken.value)
        searchingAddress.value = false
    }, 400)
})

watch(zoneRows, (rows) => {
    for (let i = 1; i < rows.length; i++) {
        rows[i].min_km = rows[i - 1].max_km ?? rows[i - 1].min_km
    }
}, { deep: true })

async function fetchData() {
    const [placesRes, zonesRes] = await Promise.all([
        client.from('provider_places').select('*').eq('provider_id', props.provider!.id).order('is_base', { ascending: false }),
        client.from('provider_pricing_zones').select('*').eq('provider_id', props.provider!.id).order('zone_number'),
    ])
    if (placesRes.data) places.value = placesRes.data as any
    if (zonesRes.data) {
        zones.value = zonesRes.data as any
        zoneRows.value = zonesRes.data.map((z: any) => ({
            min_km: z.min_km,
            max_km: z.max_km,
            one_way_price: z.one_way_price,
            round_trip_price: z.round_trip_price,
            first_leg_price: z.first_leg_price || 0,
        }))
    }
}

function openPlaceForm(place?: ProviderPlace) {
    editingPlace.value = place || null
    addressSearch.value = ''
    addressResults.value = []
    placeState.value = place
        ? { name: place.name, address: place.address || '', lat: place.lat || null, lng: place.lng || null, is_base: place.is_base, is_local_area: place.is_local_area, one_way_price: place.one_way_price ?? null, round_trip_price: place.round_trip_price ?? null }
        : { name: '', address: '', lat: null, lng: null, is_base: false, is_local_area: false, one_way_price: null, round_trip_price: null }
    showPlaceForm.value = true
}

async function selectAddress(pred: PlacePrediction) {
    addressDropdownOpen.value = false
    const details = await getPlaceDetails(pred.placeId, addressSessionToken.value)
    addressSessionToken.value = ''
    if (!details) return
    placeState.value.address = details.formatted
    placeState.value.lat = details.lat
    placeState.value.lng = details.lng
    addressSearch.value = details.formatted
}

function onAddressBlur() {
    addressDropdownOpen.value = false
}

async function savePlace() {
    if (!placeState.value.name || !props.provider) return
    savingPlace.value = true

    const payload = {
        provider_id: props.provider.id,
        name: placeState.value.name,
        address: placeState.value.address || null,
        lat: placeState.value.lat,
        lng: placeState.value.lng,
        is_base: placeState.value.is_base,
        is_local_area: placeState.value.is_local_area,
        one_way_price: placeState.value.one_way_price,
        round_trip_price: placeState.value.round_trip_price,
    }

    if (editingPlace.value) {
        const { error } = await client.from('provider_places').update(payload as never).eq('id', editingPlace.value.id)
        if (error) toast.add({ title: 'Could not update place.', color: 'red', icon: 'i-ph-warning-duotone' })
    } else {
        const { error } = await client.from('provider_places').insert(payload as never)
        if (error) toast.add({ title: 'Could not add place.', color: 'red', icon: 'i-ph-warning-duotone' })
    }

    showPlaceForm.value = false
    savingPlace.value = false
    await fetchData()
}

async function deletePlace(place: ProviderPlace) {
    await client.from('provider_places').delete().eq('id', place.id)
    await fetchData()
}

const placeActions = (place: ProviderPlace) => [[
    { label: 'Edit', icon: 'i-ph-pencil-fill', click: () => openPlaceForm(place) },
    { label: 'Remove', icon: 'i-ph-trash-fill', iconClass: 'text-red-400', click: () => deletePlace(place) },
]]

function addZoneRow() {
    const lastRow = zoneRows.value[zoneRows.value.length - 1]
    zoneRows.value.push({
        min_km: lastRow?.max_km ?? 0,
        max_km: null,
        one_way_price: 0,
        round_trip_price: 0,
        first_leg_price: 0,
    })
}

async function saveZones() {
    if (!props.provider) return
    savingZones.value = true

    await client.from('provider_pricing_zones').delete().eq('provider_id', props.provider.id)

    if (zoneRows.value.length) {
        const rows = zoneRows.value.map((row, i) => ({
            provider_id: props.provider!.id,
            zone_number: i + 1,
            min_km: row.min_km,
            max_km: row.max_km,
            one_way_price: row.one_way_price,
            round_trip_price: row.round_trip_price,
            first_leg_price: row.first_leg_price,
        }))

        const { error } = await client.from('provider_pricing_zones').insert(rows as never)
        if (error) {
            toast.add({ title: 'Could not save zones.', color: 'red', icon: 'i-ph-warning-duotone' })
        } else {
            toast.add({ title: 'Zones saved.', color: 'green', icon: 'i-ph-check-circle-duotone' })
        }
    }

    savingZones.value = false
    await fetchData()
}
</script>
